import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Cpu, Clock, TrendingUp, AlertTriangle, Brain, MessageSquare, Image as ImageIcon, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

/*
 * Compute Simulator (private, post-login).
 * Illustrates GPU-hours and indicative annual compute revenue at 15–30 % operational range.
 * Never presented on public pages.
 */

// Same illustrative economics as the previous public version — kept internal.
const H100_COST = 25000;
const HOURS_PER_MONTH = 720;
const UTIL_RATIO = 0.85;
const MARKET_RATE_HR = 3;
const YIELD_LOW = 0.15;
const YIELD_HIGH = 0.30;

function fmtInt(n) { return isFinite(n) ? Math.round(n).toLocaleString("en-US").replace(/,/g, " ") : "0"; }

function computeAll(amount) {
  const a = Math.max(0, Number(amount) || 0);
  const h100 = a / H100_COST;
  const gpuHoursMonth = h100 * HOURS_PER_MONTH * UTIL_RATIO;
  const marketValueYear = gpuHoursMonth * 12 * MARKET_RATE_HR;
  return { h100, gpuHoursMonth, marketValueYear, revLow: a * YIELD_LOW, revHigh: a * YIELD_HIGH };
}

const PRESETS = [10000, 25000, 50000, 100000];

const COPY = {
  en: {
    overline: "Compute simulator",
    title: "How your held capacity translates into compute.",
    lead: "For orientation only. Enter an amount to see the equivalent GPU-hours and the indicative annual compute revenue range operated at our Batam site.",
    label: "Amount",
    outputs: { hours: "GPU-hours per month", h100: "≈ H100-equivalent GPUs", market: "Market compute value / year", rev: "Indicative annual compute revenue" },
    useCasesTitle: "What that capacity could produce per month",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Full 70B model fine-tunes", d: "≈ 50 GPU-hours per full pass." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "AI queries served", d: "≈ 1 000 GPT-scale queries per GPU-hour." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "AI images generated", d: "≈ 50 hi-res images per GPU-hour." },
      { icon: Sparkles, key: "training", base: 720, t: "Small model trainings", d: "≈ 720 GPU-hours per 7B training." },
    ],
    footnote: "Illustrative only. Actual GPU-hours and compute revenues depend on project execution, market conditions, and the specific terms of your holding. Assumptions: H100 ≈ $25 000, 85 % utilisation, $3/h blended market rate. Indicative annual compute revenue range: 15 % – 30 %.",
    holdingLine: "You currently hold",
    units: "Titan Units",
  },
  fr: {
    overline: "Simulateur de calcul",
    title: "Ce que la capacité que vous détenez représente concrètement.",
    lead: "À titre indicatif uniquement. Saisissez un montant pour voir les heures GPU équivalentes et la fourchette indicative de revenus de calcul annuels sur notre site de Batam.",
    label: "Montant",
    outputs: { hours: "Heures GPU par mois", h100: "≈ Équivalent GPU H100", market: "Valeur marché de calcul / an", rev: "Revenu de calcul annuel indicatif" },
    useCasesTitle: "Ce que cette capacité pourrait produire par mois",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Fine-tunes complets 70B", d: "≈ 50 heures GPU par passe complète." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "Requêtes IA servies", d: "≈ 1 000 requêtes de niveau GPT par heure GPU." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "Images IA générées", d: "≈ 50 images haute résolution par heure GPU." },
      { icon: Sparkles, key: "training", base: 720, t: "Entraînements de petits modèles", d: "≈ 720 heures GPU par entraînement 7B." },
    ],
    footnote: "Illustratif uniquement. Les heures GPU et revenus de calcul réels dépendent de l'exécution, des conditions de marché et des termes propres à votre participation. Hypothèses : H100 ≈ 25 000 $, 85 % d'utilisation, taux marché mixte 3 $/h. Fourchette indicative de revenus de calcul annuels : 15 % – 30 %.",
    holdingLine: "Vous détenez actuellement",
    units: "Titan Units",
  },
  es: {
    overline: "Simulador de cálculo",
    title: "Lo que la capacidad que dispones representa.",
    lead: "Solo orientativo. Introduce un importe para ver las horas GPU equivalentes y el rango indicativo de ingresos anuales de cálculo en nuestro sitio de Batam.",
    label: "Importe",
    outputs: { hours: "Horas GPU al mes", h100: "≈ GPU equivalentes H100", market: "Valor de mercado de cálculo / año", rev: "Ingreso anual de cálculo indicativo" },
    useCasesTitle: "Lo que esa capacidad podría producir por mes",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Fine-tunes completos 70B", d: "≈ 50 horas GPU por pasada completa." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "Consultas IA servidas", d: "≈ 1 000 consultas nivel GPT por hora GPU." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "Imágenes IA generadas", d: "≈ 50 imágenes de alta resolución por hora GPU." },
      { icon: Sparkles, key: "training", base: 720, t: "Entrenamientos de modelos pequeños", d: "≈ 720 horas GPU por modelo 7B." },
    ],
    footnote: "Solo ilustrativo. Las horas GPU e ingresos de cálculo reales dependen de la ejecución, condiciones de mercado y términos específicos. Hipótesis: H100 ≈ 25 000 $, 85 % de utilización, tarifa mercado mixta 3 $/h. Rango anual indicativo de ingresos de cálculo: 15 % – 30 %.",
    holdingLine: "Dispones actualmente de",
    units: "Titan Units",
  },
};

export default function Simulator() {
  const { api, token } = useAuth();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [amount, setAmount] = useState(25000);
  const [heldUnits, setHeldUnits] = useState(null);
  const r = useMemo(() => computeAll(amount), [amount]);

  useEffect(() => {
    axios.get(`${api}/me/units`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setHeldUnits(res.data.titan_units))
      .catch(() => {});
  }, [api, token]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <p className="overline text-muted-foreground">{c.overline}</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">{c.title}</h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">{c.lead}</p>

      {heldUnits !== null && (
        <p className="mt-4 text-[13px] text-muted-foreground" data-testid="simulator-holding-line">
          {c.holdingLine}{" "}
          <span className="font-display text-foreground">{Number(heldUnits).toLocaleString("fr-FR").replace(/,/g, " ")}</span>{" "}
          {c.units}.
        </p>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Input */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 md:p-8">
          <p className="text-[11px] tracking-widest uppercase text-muted-foreground">Presets</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {PRESETS.map(p => (
              <button
                key={p}
                data-testid={`sim-preset-${p}`}
                onClick={() => setAmount(p)}
                className={`px-3 py-1.5 rounded-full text-[12px] tracking-wide border transition-colors ${amount === p ? "bg-emerald-500 text-white border-emerald-500" : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}
              >${(p / 1000)}k</button>
            ))}
          </div>
          <div className="mt-8">
            <p className="text-[11px] tracking-widest uppercase text-muted-foreground">{c.label} ($)</p>
            <input
              type="number" min="1000" step="1000" value={amount} onChange={e => setAmount(e.target.value)}
              data-testid="sim-amount"
              className="mt-2 w-full font-display text-3xl md:text-4xl tracking-tighter bg-transparent outline-none py-2 border-b border-border focus:border-foreground transition-colors text-foreground"
            />
            <input
              type="range" min="1000" max="250000" step="1000" value={amount} onChange={e => setAmount(e.target.value)}
              data-testid="sim-range" className="mt-6 w-full accent-emerald-500"
            />
            <div className="mt-2 flex justify-between text-[10px] tracking-widest uppercase text-muted-foreground">
              <span>$1k</span><span>$250k</span>
            </div>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-3 rounded-2xl border border-border bg-gradient-to-br from-emerald-500/10 via-transparent to-sky-500/10 p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Metric icon={Clock} label={c.outputs.hours} value={fmtInt(r.gpuHoursMonth)} testId="sim-out-hours" />
            <Metric icon={Cpu} label={c.outputs.h100} value={r.h100.toFixed(2).replace(/\.00$/, "")} testId="sim-out-h100" />
            <Metric icon={TrendingUp} label={c.outputs.market} value={`$${fmtInt(r.marketValueYear)}`} testId="sim-out-market" />
            <Metric icon={TrendingUp} label={c.outputs.rev} value={`$${fmtInt(r.revLow)} — $${fmtInt(r.revHigh)}`} testId="sim-out-rev" accent />
          </div>

          <div className="mt-10 pt-8 border-t border-border">
            <p className="text-[11px] tracking-widest uppercase text-muted-foreground">{c.useCasesTitle}</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {c.useCases.map((u, i) => {
                const I = u.icon;
                const qty = Math.floor(r.gpuHoursMonth / u.base);
                return (
                  <motion.div
                    key={u.key}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    data-testid={`sim-usecase-${u.key}`}
                    className="rounded-xl p-4 flex items-start gap-3 bg-card/50 border border-border"
                  >
                    <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <I className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-display text-2xl tracking-tighter">{fmtInt(qty)}</p>
                      <p className="text-[13px] leading-snug text-muted-foreground">{u.t}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground/60">{u.d}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-start gap-3 text-[11px] leading-relaxed max-w-3xl text-muted-foreground">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
        <p>{c.footnote}</p>
      </div>
    </motion.div>
  );
}

const Metric = ({ icon: I, label, value, testId, accent = false }) => (
  <div data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-muted-foreground">
      <I className="w-3.5 h-3.5" /> {label}
    </div>
    <p className={`mt-3 font-display font-light tracking-tighter text-3xl md:text-4xl leading-none ${accent ? "text-emerald-400" : "text-foreground"}`}>{value}</p>
  </div>
);
